import { tenantPalettes } from 'aos-ui-universal/src/theme/figma/tenant-palettes'
import { appConfig } from 'appConfig'

const BaseColors = {
    Black: '#050a10',
    White: '#ffffff',
    WhiteOpacity45: 'rgba(255,255,255, .45)',

    Grey: '#69728B',
    Grey1: '#f8f8f8',
    Grey2: '#858891',
    Grey4: '#B1B4BD',
    Grey6: '#707688',
    Blue: '#1263FF',

    Transparent: 'rgba(0,0,0,0)',
    TransparentBlack: 'rgba(0,0,0,0.6)',
    TransparentGrey: '#E5E6EA',

    DarkGreen: '#0a6e2c',
    LightGreen: '#45eb4d',
    SoftGreen: '#81c48d',
    DarkYellow: '#664A02',
    PaleOrange: '#f1a267',
    DarkRed: '#280006',
    Magenta: '#da58ab',
    SoftMagenta: '#d958ab',
    Violet: '#6c55a7',
    SoftBlue: '#4c85ff',
    SeaBlue: '#4dc8c9',
    TaskBlue: '#1A62FB',
    InfoToastBlue: '#005de5',
    ProgressGreen: '#69F996',
    ProgressYellow: '#FFB904',
    UnitTaskBlack: '#16181C',
    UnitTaskYellow: '#332500',
    UnitTaskGreen: '#163320',
    UnitTaskRed: '#140001',
}

const neutralAliases = (palette: (typeof tenantPalettes)[keyof typeof tenantPalettes]) => ({
    BackgroundPrimary: palette['aos-bg'].canvas,
    TimelineBackground: palette['aos-bg'].canvas,
    BackgroundDark: palette['aos-bg'].surface,
    ChartBase: palette.border.subtle,
    ChartAxisLineStroke: palette.border.subtle,
    FormListSeparator: palette.border.subtle,
    ReadOnlyInputBase: palette.border.subtle,
    TextSecondary: palette['aos-text'].muted,
    MenuButtonColor: palette['aos-text'].default,
    SideMenuInactive: palette['aos-text'].default,
    MobileFlightInfoNatureOfFlightBase: palette['aos-text'].default,
})

const statusAliases = (palette: (typeof tenantPalettes)[keyof typeof tenantPalettes]) => ({
    Red: palette.status.critical,
    Yellow: palette.status.warning,
    Green: palette.status.success,
    Orange: palette.status.caution,
})

const AosColors = {
    ...statusAliases(tenantPalettes.aos),
    ...neutralAliases(tenantPalettes.aos),
    //Primary colors
    Primary: tenantPalettes.aos['aos-primary'].default,
    PrimaryLight: '#417eff',
    PrimaryDark: '#0f4aca',
    PrimaryPale: '#7ca6ff',

    BackgroundCanvas: tenantPalettes.aos['aos-bg'].canvas,
    BackgroundElevation: tenantPalettes.aos['aos-bg'].elevation,
    ContentOnGraphics: tenantPalettes.aos['aos-text']['on-graphics'],
    StatusSuccess: tenantPalettes.aos.status.success,
    StatusWarning: tenantPalettes.aos.status.warning,
    StatusCritical: tenantPalettes.aos.status.critical,
    StatusCaution: tenantPalettes.aos.status.caution,
    StatusNeutral: tenantPalettes.aos.status.neutral,
    RemoveAccountBackground: BaseColors.White,
    RemoveAccountBorder: '#b9b9b9',

    TextPrimary: '#FFFFFF',
    TextDarkPrimary: '#14202C',
    TextDarkSecondary: '#5C6B7A',

    IconDarkPrimary: tenantPalettes.aos['aos-text']['on-graphics'],

    //App specific colors

    MobileReportingItem: '#182146',

    WidgetSecondary: tenantPalettes.aos['aos-text'].muted,
    WidgetPrimary: tenantPalettes.aos['aos-text'].default,
    DisabledText: '#c6c7cb', //Grey2
    FlightGrey: 'rgb(61,75,107)',
    FlightGrey2: 'rgb(104, 128, 170)',
    ToastFont: tenantPalettes.aos['aos-text']['on-graphics'],
    Tint: '#417eff',
    LightScrollbar: 'rgba(193, 193, 193, .8)',
    DashboardWarningBadge: tenantPalettes.aos.status.warning,

    MapBorder: 'rgba(245, 247, 156, 1)',
    TransparentButtonBorder: BaseColors.White,
    TransparentButtonText: BaseColors.White,
    LinkTextLight: '#7ca6ff',
    ChartAxisLabel: '#69728b',
    ThemeOnWhite: '#005de5',
    AddAttachmentButtonContent: BaseColors.White,

    CheckboxPrimary: tenantPalettes.aos.action.default,
    CheckboxInactiveLight: BaseColors.Grey2,
    CheckboxInactiveDark: BaseColors.Grey2,

    Onboarding: '#293158',

    OnboardingGradient1: '#005de5',
    OnboardingGradient2: '#0f4aca',
}

const AoimsColors = {
    ...statusAliases(tenantPalettes.aoims),
    ...neutralAliases(tenantPalettes.aoims),
    PrimaryLight: '#FFDD4F',
    Primary: tenantPalettes.aoims['aos-primary'].default,
    PrimaryDark: '#b08f22',
    PrimaryPale: '#2D2D2D',

    BackgroundCanvas: tenantPalettes.aoims['aos-bg'].canvas,
    BackgroundElevation: tenantPalettes.aoims['aos-bg'].elevation,
    ContentOnGraphics: tenantPalettes.aoims['aos-text']['on-graphics'],
    StatusSuccess: tenantPalettes.aoims.status.success,
    StatusWarning: tenantPalettes.aoims.status.warning,
    StatusCritical: tenantPalettes.aoims.status.critical,
    StatusCaution: tenantPalettes.aoims.status.caution,
    StatusNeutral: tenantPalettes.aoims.status.neutral,
    RemoveAccountBackground: '#EAEEEF',
    RemoveAccountBorder: '#b9b9b9',

    TextPrimary: '#FFFFFF',
    TextDarkPrimary: '#0F0F0F',
    TextDarkSecondary: '#858891',

    MobileReportingItem: '#2D2D2D',

    IconDarkPrimary: tenantPalettes.aoims['aos-text']['on-graphics'],

    WidgetSecondary: tenantPalettes.aoims['aos-text'].muted,
    WidgetPrimary: tenantPalettes.aoims['aos-text'].default,

    DisabledText: '#c6c7cb',
    FlightGrey: '#B9B9B9',
    FlightGrey2: '#B9B9B9',

    ToastFont: tenantPalettes.aoims['aos-text']['on-graphics'],
    Tint: '#2D2D2D',
    LightScrollbar: 'rgba(193, 193, 193, .8)',
    DashboardWarningBadge: '#FF903C',

    MapBorder: 'rgba(45, 142, 156, 1)',

    TransparentButtonBorder: BaseColors.Black,
    TransparentButtonText: BaseColors.Black,
    LinkTextLight: '#b08f22',
    ChartAxisLabel: '#EAEAEA',
    ThemeOnWhite: '#b08f22',
    AddAttachmentButtonContent: BaseColors.White,
    CheckboxPrimary: tenantPalettes.aoims.action.default,
    CheckboxInactiveLight: '#B9B9B9',
    CheckboxInactiveDark: '#604a14',

    Onboarding: '#D5D3D1',
    OnboardingGradient1: '#EAEEEF',
    OnboardingGradient2: '#D5D3D1',
}

// Swedavia palette. Structurally identical to AoimsColors (single accent on
// a dark background) so every key required by the design system stays
// defined. The accent, the status colours and the neutral members come from the
// Swedavia Figma palette; the rest of the keys inherit the AOIMS values.
// Values come from the Figma mirror, which is `as const`, so a key that differs
// between tenants carries a literal type. Keying on AosColors for completeness
// while typing the values as string keeps every tenant required to define the
// same keys without forcing them to the same colour.
type TenantColorSet = Record<keyof typeof AosColors, string>

const SwedaviaColors: TenantColorSet = {
    ...AoimsColors,
    ...statusAliases(tenantPalettes.swedavia),
    ...neutralAliases(tenantPalettes.swedavia),

    Primary: tenantPalettes.swedavia['aos-primary'].default,
    PrimaryLight: tenantPalettes.swedavia['aos-primary'].hover,
    PrimaryDark: tenantPalettes.swedavia['aos-primary'].pressed,
    PrimaryPale: tenantPalettes.swedavia['aos-primary'].surface,

    BackgroundCanvas: tenantPalettes.swedavia['aos-bg'].canvas,
    BackgroundElevation: tenantPalettes.swedavia['aos-bg'].elevation,
    ContentOnGraphics: tenantPalettes.swedavia['aos-text']['on-graphics'],
    StatusSuccess: tenantPalettes.swedavia.status.success,
    StatusWarning: tenantPalettes.swedavia.status.warning,
    StatusCritical: tenantPalettes.swedavia.status.critical,
    StatusCaution: tenantPalettes.swedavia.status.caution,
    StatusNeutral: tenantPalettes.swedavia.status.neutral,

    // Links and active tab underline, on the accent family.
    LinkTextLight: tenantPalettes.swedavia['aos-primary'].hover,
    ThemeOnWhite: tenantPalettes.swedavia['aos-primary'].pressed,

    CheckboxPrimary: tenantPalettes.swedavia.action.default,
}

export const ColorConfig: Record<string, TenantColorSet> = {
    AOS: AosColors,
    AOIMS: AoimsColors,
    SWEDAVIA: SwedaviaColors,
}

export const Color = {
    ...BaseColors,
    ...ColorConfig[appConfig.theme],
}

// The app chrome is a dark surface on every tenant since the redesign, so the OS
// status bar always needs light content. AOIMS used to render a light/yellow
// chrome, hence the old per-tenant map — on the dark header it put black glyphs
// on a near-black bar (AOS-831).
export const statusBarColor = 'light-content'

export type Color =
    | (typeof BaseColors)[keyof typeof BaseColors]
    | (typeof AosColors)[keyof typeof AosColors]
    | (typeof AoimsColors)[keyof typeof AoimsColors]
